import { useMemo, useState } from 'react';
import { LatLng, haversineKm } from './geoHelpers';
import useQueryApiClient from './useQueryApiClient';

interface RoadDistance {
  infrastructure_id: number;
  distance_meters: number;
  duration_seconds: number;
}

export interface RouteEstimate {
  km: number;
  minutes: number | null;
}

interface RoadDistancesResult {
  url: string;
  byId: Map<number, RouteEstimate>;
}

export type RouteEstimateResolver = (
  infrastructureId: number,
  point: LatLng | null
) => RouteEstimate | null;

const toEstimates = (response: unknown) =>
  new Map<number, RouteEstimate>(
    (Array.isArray(response) ? (response as RoadDistance[]) : []).map(
      (item) => [
        item.infrastructure_id,
        {
          km: item.distance_meters / 1000,
          minutes: Math.max(1, Math.round(item.duration_seconds / 60)),
        },
      ]
    )
  );

export const useRoadDistances = (
  farmId: number | undefined,
  location: LatLng | null
): RouteEstimateResolver => {
  const url =
    farmId && location
      ? `api/v2/farms/${farmId}/road-distances?lat=${location.lat}&lng=${location.lng}`
      : '';

  const [result, setResult] = useState<RoadDistancesResult>({
    url: '',
    byId: new Map(),
  });

  useQueryApiClient({
    request: { url, disableOnMount: !url },
    onSuccess: (response) => setResult({ url, byId: toEstimates(response) }),
    onError: () => setResult({ url, byId: new Map() }),
  });

  return useMemo(() => {
    const settled = result.url === url;

    return (infrastructureId, point) => {
      if (!location || !point || !settled) return null;

      return (
        result.byId.get(infrastructureId) ?? {
          km: haversineKm(location, point),
          minutes: null,
        }
      );
    };
  }, [result, url, location]);
};
